import React, { useMemo, useState } from "react";
import NumberFormat from "react-number-format";

import { Autocomplete, Button, Stack, TextField, Typography } from "@mui/material";
import { indexNames } from "constants/reports";
import { IAssetClass } from "store/types";

interface SelectedCellContentProps {
  onCancel: () => void;
  onSubmit: (selectedIndex: { value: string | number | { id: number; name: string } | null }) => void;
  selectedCellData: {
    assetClass: IAssetClass;
    column: "private_index" | "public_index" | "expected_return";
  } | null;
  privateIndexOptions?: { id: number; name: string }[] | null;
}

export const SelectedCellContent: React.FC<SelectedCellContentProps> = ({
  onCancel,
  onSubmit,
  selectedCellData,
  privateIndexOptions
}) => {
  const content = useMemo(() => {
    switch (selectedCellData?.column) {
      case "public_index":
        return (
          <SelectedPublicIndexContent onCancel={onCancel} onSubmit={onSubmit} selectedCellData={selectedCellData} />
        );
      case "private_index":
        return (
          <SelectedPrivateIndexContent
            onCancel={onCancel}
            onSubmit={onSubmit}
            selectedCellData={selectedCellData}
            privateIndexOptions={privateIndexOptions}
          />
        );
      case "expected_return":
        return (
          <SelectedExpectedReturnContent onCancel={onCancel} onSubmit={onSubmit} selectedCellData={selectedCellData} />
        );
      default:
        return null;
    }
  }, [selectedCellData?.column]);
  if (!content) return null;

  return <>{content}</>;
};

const SelectedPublicIndexContent: React.FC<SelectedCellContentProps> = ({ onCancel, onSubmit, selectedCellData }) => {
  const [selectedIndexData, setSelectedIndexData] = useState<string | null>(
    selectedCellData?.assetClass?.public_index || null
  );

  const options = Object.keys(indexNames).map((key) => ({
    key,
    label: indexNames[key]
  }));
  return (
    <Stack sx={{ borderRadius: "8px", boxShadow: "0px 0px 10px 0px rgba(0, 0, 0, 0.1)", padding: "20px" }} gap="20px">
      <Typography>Select Index</Typography>

      <Stack direction="row" gap="20px" alignItems="center">
        <Autocomplete
          sx={{ width: "300px" }}
          options={options}
          value={selectedIndexData ? { key: selectedIndexData, label: indexNames[selectedIndexData] } : null}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.key === value.key}
          onChange={(_, value) => setSelectedIndexData(value ? value.key : null)}
          renderInput={(params) => <TextField {...params} label="Indexes" placeholder="Search..." />}
          filterOptions={(options, { inputValue }) => {
            const searchTerm = inputValue.toLowerCase();
            return options.filter(
              (option) =>
                option.label.toLowerCase().includes(searchTerm) || option.key.toLowerCase().includes(searchTerm)
            );
          }}
        />

        <Button
          onClick={onCancel}
          variant="outlined"
          sx={{
            borderColor: "#ddd",
            color: "#333",
            "&:hover": {
              borderColor: "#adb5bd",
              backgroundColor: "#f8f9fa"
            }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={(e) => {
            e.stopPropagation();

            onSubmit({ value: selectedIndexData });
          }}
          variant="contained"
        >
          Add
        </Button>
      </Stack>
    </Stack>
  );
};

const SelectedPrivateIndexContent: React.FC<SelectedCellContentProps> = ({
  onCancel,
  onSubmit,
  selectedCellData,
  privateIndexOptions
}) => {
  const [selectedIndexData, setSelectedIndexData] = useState<{ id: number; name: string } | null>(
    selectedCellData?.assetClass?.private_index || null
  );

  return (
    <Stack sx={{ borderRadius: "8px", boxShadow: "0px 0px 10px 0px rgba(0, 0, 0, 0.1)", padding: "20px" }} gap="20px">
      <Typography>Select Index</Typography>

      <Stack direction="row" gap="20px" alignItems="center">
        <Autocomplete
          sx={{ width: "300px" }}
          options={privateIndexOptions || []}
          value={selectedIndexData ? { id: selectedIndexData.id, name: selectedIndexData.name } : null}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(_, value) => setSelectedIndexData(value ? { id: value.id, name: value.name } : null)}
          renderInput={(params) => <TextField {...params} label="Indexes" placeholder="Search..." />}
          filterOptions={(options, { inputValue }) => {
            const searchTerm = inputValue.toLowerCase();
            return options.filter(
              (option) => option.name.toLowerCase().includes(searchTerm) || option.id.toString().includes(searchTerm)
            );
          }}
        />

        <Button
          onClick={onCancel}
          variant="outlined"
          sx={{
            borderColor: "#ddd",
            color: "#333",
            "&:hover": {
              borderColor: "#adb5bd",
              backgroundColor: "#f8f9fa"
            }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={(e) => {
            e.stopPropagation();
            onSubmit({ value: selectedIndexData });
          }}
          variant="contained"
        >
          Add
        </Button>
      </Stack>
    </Stack>
  );
};

const SelectedExpectedReturnContent: React.FC<SelectedCellContentProps> = ({
  onCancel,
  onSubmit,
  selectedCellData
}) => {
  const [selectedExpectedReturnData, setSelectedExpectedReturnData] = useState<number | null>(
    typeof selectedCellData?.assetClass?.expected_return === "number"
      ? selectedCellData?.assetClass?.expected_return
      : null
  );

  return (
    <Stack sx={{ borderRadius: "8px", boxShadow: "0px 0px 10px 0px rgba(0, 0, 0, 0.1)", padding: "20px" }} gap="20px">
      <Typography>Expected Return</Typography>

      <Stack direction="row" gap="20px" alignItems="center">
        <NumberFormat
          thousandSeparator
          isNumericString
          variant="outlined"
          suffix="%"
          size="small"
          sx={{ width: "300px" }}
          decimalScale={2}
          label="Expected Return"
          customInput={TextField}
          value={selectedExpectedReturnData}
          onValueChange={({ value }) => setSelectedExpectedReturnData(value === "" ? null : Number(value))}
        />

        <Button
          onClick={onCancel}
          variant="outlined"
          sx={{
            borderColor: "#ddd",
            color: "#333",
            "&:hover": {
              borderColor: "#adb5bd",
              backgroundColor: "#f8f9fa"
            }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={(e) => {
            e.stopPropagation();
            onSubmit({ value: selectedExpectedReturnData });
          }}
          variant="contained"
        >
          Add
        </Button>
      </Stack>
    </Stack>
  );
};
